import { createContext, useContext, useEffect, useRef } from "react";

/** A page inside a section, which takes over the section's header: its title, and where back goes. */
export interface SubPageHeader {
  title: string;
  onBack: () => void;
  /** How many pages deep it is, so moving between pages knows which way to slide. */
  depth: number;
}

/**
 * How a page inside a section talks to the layout around it: `set` puts its title and back action
 * in the header, and `prepare` is called just before moving to another page, so the layout can keep
 * a picture of this one to slide away.
 */
interface SubPageApi {
  set: (header: SubPageHeader | null) => void;
  prepare: () => void;
}

export const SubPage = createContext<SubPageApi>({ set: () => {}, prepare: () => {} });

/** Moves to another page inside a section, after the layout has kept a picture of this one. */
export function useSubPageMove() {
  return useContext(SubPage).prepare;
}

/** Puts this page's title and back action in the section's header while it is shown. */
export function useSubPage(title: string, depth: number, onBack: () => void) {
  const { set } = useContext(SubPage);
  const back = useRef(onBack);
  back.current = onBack;
  useEffect(() => {
    set({ title, depth, onBack: () => back.current() });
    return () => set(null);
  }, [title, depth, set]);
}
